/** Commission report (ST-04 partial, BA-04 attribution): per-staff per-basis
 *  earned commissions over a date range, with a client-side CSV export. Amounts
 *  render as MKD via the shared formatter. */
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CurrencyDisplay, Spinner } from '../../components/ui.js';

import { type CommissionSummaryRow, useCommissionSummary } from './hooks/useStaff.js';

function monthStart(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
}

export interface CommissionReportProps {
  staffId?: string;
}

export function CommissionReport({ staffId }: CommissionReportProps): ReactNode {
  const { t } = useTranslation();
  const [from, setFrom] = useState(monthStart().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const summary = useCommissionSummary({
    from: new Date(from).toISOString(),
    to: new Date(to + 'T23:59:59Z').toISOString(),
    ...(staffId ? { staffId } : {}),
  });

  const rows: CommissionSummaryRow[] = summary.data ?? [];
  const total = rows.reduce((s, r) => s + r.total_amount, 0);

  function exportCsv(): void {
    const header = ['staff_name', 'app_role', 'basis', 'transaction_count', 'total_amount'];
    const lines = rows.map((r) =>
      [r.staff_name, r.app_role, r.basis, r.transaction_count, r.total_amount].join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'commissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', marginBottom: '0.75rem' }}>
        <h2 style={{ margin: 0, flex: 1 }}>{t('staff.commissions.title')}</h2>
        <label>
          {t('staff.commissions.date_from')}
          <input
            type="date"
            aria-label="from"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label>
          {t('staff.commissions.date_to')}
          <input type="date" aria-label="to" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <Button variant="secondary" onClick={exportCsv}>
          {t('staff.commissions.export_csv')}
        </Button>
      </div>

      {summary.isLoading ? (
        <Spinner />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-muted)' }}>
              <th>{t('staff.title')}</th>
              <th>{t('staff.commissions.title')}</th>
              <th>{t('staff.commissions.transactions')}</th>
              <th>{t('staff.commissions.total')}</th>
              <th>{t('staff.commissions.paid_out')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.staff_id}-${r.basis}`}
                style={{ borderTop: '1px solid var(--color-border)' }}
              >
                <td>{r.staff_name}</td>
                <td>{t('staff.commissions.basis.' + r.basis)}</td>
                <td>{r.transaction_count}</td>
                <td>
                  <CurrencyDisplay minorUnits={r.total_amount} />
                </td>
                <td>{r.fully_paid_out ? t('common.yes') : t('common.no')}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: 600 }}>
              <td colSpan={3}>{t('staff.commissions.total')}</td>
              <td colSpan={2} data-testid="commission-grand-total">
                <CurrencyDisplay minorUnits={total} />
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </Card>
  );
}
